import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, MessageSquarePlus, QrCode, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth, teksRalat } from "../lib/auth";
import { useSeo } from "../lib/seo";

const LBL = "text-[15px] font-semibold text-[#141B24]";
const FLD =
  "mt-2.5 h-[52px] rounded-[12px] border-[#E6E8EC] bg-white px-4 text-[15px] placeholder:text-[#9CA3AF] focus-visible:ring-2 focus-visible:ring-[#00A0E9] focus-visible:ring-offset-2";

const Pemisah = ({ teks }) => (
  <div className="relative my-7">
    <div className="h-px bg-[#E6E8EC]" />
    <span className="absolute left-1/2 -top-2.5 -translate-x-1/2 bg-white px-3 text-[13px] font-medium text-[#6B7280]">
      {teks}
    </span>
  </div>
);

export default function Login() {
  useSeo({ tajuk: "Log Masuk", keterangan: "Log masuk pasukan kempen untuk urus aduan rakyat dan pantau prestasi zon P.061 Padang Rengas.", laluan: "/login" });
  const { masuk } = useAuth();
  const navigate = useNavigate();
  const [id, setId] = useState("");
  const [kl, setKl] = useState("");
  const [ralat, setRalat] = useState("");
  const [sibuk, setSibuk] = useState(false);

  const hantar = async (e) => {
    e?.preventDefault();
    if (!id.trim() || !kl) return setRalat("Sila isi kedua-dua medan.");
    setSibuk(true);
    setRalat("");
    try {
      await masuk(id.trim(), kl);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setRalat(teksRalat(err?.response?.data?.detail, err?.message));
    } finally {
      setSibuk(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-[#FAFAFA] px-4 py-10 sm:py-14">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(520px 320px at 72% 14%, rgba(0,174,239,0.14) 0%, rgba(0,114,188,0.06) 38%, rgba(255,255,255,0) 72%)",
        }}
      />

      <div className="relative mx-auto w-full max-w-[452px]">
      <div className="overflow-hidden rounded-[20px] border border-[#E6E8EC] bg-white shadow-[0_10px_28px_rgba(16,24,40,0.08)]">
          <div className="jalur-pkr h-1.5 w-full" aria-hidden="true" />
          <div className="p-6 sm:p-9">
          {/* Baris brand, dijajar tengah */}
          <div className="flex flex-col items-center gap-3">
            <img
              src="/logo/lockup.png"
              alt="Kamil Munim"
              width={1697}
              height={402}
              className="h-8 w-auto max-w-full object-contain"
            />
            <div className="flex items-center gap-3">
              <p className="font-mono-plex text-[11px] uppercase tracking-[0.18em] text-[#6B7280]">
                P.061 Padang Rengas
              </p>
              <span className="h-4 w-px bg-[#E6E8EC]" />
              <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-[color:var(--maroon)]">
                Log Masuk
              </p>
            </div>
          </div>

          <h1 className="font-display mt-6 text-center text-3xl font-bold tracking-tight text-[#141B24] sm:text-4xl">
            Selamat kembali
          </h1>
          <p className="mx-auto mt-2.5 max-w-[360px] text-center text-sm leading-relaxed text-[#6B7280]">
            Log masuk untuk urus aduan rakyat, pantau prestasi zon dan sediakan laporan kempen P.061
            Padang Rengas.
          </p>

          <form onSubmit={hantar} className="mt-7">
            <div>
              <Label htmlFor="login-id" className={LBL}>
                Emel atau nama pengguna
              </Label>
              <Input
                id="login-id"
                data-testid="login-input-id"
                className={FLD}
                placeholder="admin atau admin@padangrengas.my"
                value={id}
                onChange={(e) => setId(e.target.value)}
                autoComplete="username"
              />
            </div>

            <div className="mt-6">
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="login-kl" className={LBL}>
                  Kata laluan
                </Label>
                <span className="text-[13px] text-[#9CA3AF]">Hubungi admin jika terlupa</span>
              </div>
              <Input
                id="login-kl"
                type="password"
                data-testid="login-input-kata-laluan"
                className={FLD}
                placeholder="Masukkan kata laluan"
                value={kl}
                onChange={(e) => setKl(e.target.value)}
                autoComplete="current-password"
              />
            </div>

            {ralat && (
              <p
                className="mt-5 rounded-xl border border-[#F6D6D9] bg-[#FDF3F4] px-4 py-3 text-sm text-[#A5151A]"
                data-testid="login-error"
              >
                {ralat}
              </p>
            )}

            <Button
              type="submit"
              disabled={sibuk}
              data-testid="login-submit-button"
              className="mt-7 h-[52px] w-full rounded-[12px] bg-[#072F4A] text-[15px] font-semibold text-white transition-colors hover:bg-[#052A42] focus-visible:ring-2 focus-visible:ring-[#00A0E9] focus-visible:ring-offset-2 active:scale-[0.99] disabled:opacity-70"
            >
              {sibuk ? "Menyemak..." : "Log masuk"}
              <ArrowRight className="ml-2 h-[18px] w-[18px]" />
            </Button>
          </form>

          <Pemisah teks="Untuk rakyat" />

          <div className="grid grid-cols-2 gap-3">
            <a
              href="/aduan"
              data-testid="login-aduan-link"
              className="flex flex-col items-center rounded-[14px] border border-[#E6E8EC] bg-white px-4 py-5 text-center transition-colors hover:border-[#DDE3EA] hover:bg-[#FAFAFA] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00A0E9]"
            >
              <MessageSquarePlus className="h-5 w-5 text-[#141B24]" />
              <p className="mt-2.5 text-sm font-semibold text-[#141B24]">Hantar aduan</p>
              <p className="mt-1 text-xs leading-relaxed text-[#6B7280]">Tanpa perlu akaun</p>
            </a>
            <a
              href="/poster"
              data-testid="login-poster-link"
              className="flex flex-col items-center rounded-[14px] border border-[#E6E8EC] bg-white px-4 py-5 text-center transition-colors hover:border-[#DDE3EA] hover:bg-[#FAFAFA] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00A0E9]"
            >
              <QrCode className="h-5 w-5 text-[#141B24]" />
              <p className="mt-2.5 text-sm font-semibold text-[#141B24]">Poster QR</p>
              <p className="mt-1 text-xs leading-relaxed text-[#6B7280]">Cetak untuk kampung</p>
            </a>
          </div>

          <p className="mt-7 text-center text-[13px] text-[#6B7280]">
            Belum ada akaun pasukan?{" "}
            <a
              href="/aduan"
              data-testid="login-hubungi-link"
              className="font-semibold text-[#00679B] underline decoration-[#00A0E9] decoration-2 underline-offset-2"
            >
              Hantar aduan sebagai rakyat
            </a>
          </p>
          </div>
        </div>

        <div
          className="mt-4 flex items-start gap-2.5 rounded-[16px] border border-[#E6E8EC] bg-[#FAFAFA] px-4 py-3.5"
          data-testid="login-notis-keselamatan"
        >
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#0072BC]" />
          <p className="text-[11px] leading-relaxed text-[#6B7280]">
            Akses terhad kepada pasukan yang dibenarkan. Semua tindakan direkod dalam log audit.
            Kata laluan mesti sekurang-kurangnya 12 aksara. Jika anda rakyat yang ingin membuat
            aduan, gunakan borang aduan awam di atas.
          </p>
        </div>

        <p className="mt-6 text-center text-[11px] text-[#9CA3AF]">
          Dashboard Kempen, P.061 Padang Rengas, Kuala Kangsar, Perak.
        </p>
      </div>
    </div>
  );
}
